/*
* User edit view for authenticated users
*/
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import Branding from '../branding/branding';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import * as UserComponents from '../common/user-components';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import { AuthConfig } from '../config/authconfiguration';
import * as DetailFormCommon from '../inventory/detail-form/common';
import { PortalRoles } from '../invite/create-invite';
import * as ReduxQueries from '../queries';
import { UserUtilities } from './user-utilities';

export const DefaultNewUserRoleIds: Array<string> = ['5b5c9e45171a7d764a7241c3']; // Employee role

export interface IUserEditProps extends AppComp.IAppComponentProps {
    userDetail?: any;
    userObjId?: any;
    updateUser?: any;
    newUser?: any;
    newUserResume?: any;
    navigateTo?: any;
    userResume?: any;
    regions?: any;
    roles?: any;
    users?: any;
    portalInvites?: any;
    usersCurrentGroups?: any;
    allGroups?: any;
}

export interface IUserEditState extends AppComp.IAppComponentState {
    formChanges?: any;
    showModalResetPassword?: boolean;
    showModalNewUser?: boolean;
    showModalModifyGroups?: boolean;
}

class EditUser extends AppComp.AppComponent<IUserEditProps, IUserEditState> {
    public originalFormDataCache = {};

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: IUserEditProps) {
        super(props, 'app.user-detail.');

        this.state = {
            formChanges: {},
            showModalResetPassword: false,
            showModalNewUser: false,
            showModalModifyGroups: false,
        };
    }

    public componentDidMount() {
        const targetUserObjId = Formatters.getStr(this.props, 'userObjId');

        const enforce = this.enforceAdminOnly();

        if (enforce) {
            this.props.navigateTo(NavigationLinks.userDashboard());
        }
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // TRANSFORMS

    // HANDLERS
    public handleShowModalResetPassword(e: any, formData: any) {
        this.setState({ showModalResetPassword: true });
    }

    public handleCloseModalResetPassword() {
        this.setState({ showModalResetPassword: false });
    }

    public handleSaveButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { user: _.pick(this.props.userDetail, ['username', '_id']) },
            this.state.formChanges
        );

        this.props.updateUser(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({ formChanges: {} });
            } else {
                // Error during creation...
            }
        });
        // Force Refresh / Return to View Profile
        /* const userObjId = Formatters.getStr(this.props, 'userObjId');
        this.props.navigateTo(NavigationLinks.userEditObjId(userObjId)); */
        const ud = Formatters.getStr(this.props, 'userDetail._id');
        this.props.navigateTo(NavigationLinks.userDetailObjId(ud));
    }

    public handleSearchUsersButton(e: any) {
        this.props.navigateTo(NavigationLinks.userSearch());
    }

    public handleViewProfileButton(e: any) {
        const ud = Formatters.getStr(this.props, 'userDetail._id');
        this.props.navigateTo(NavigationLinks.userDetailObjId(ud));
    }

    public handleCreateNewUserButton(e: any) {
        this.props.navigateTo(NavigationLinks.userAdd());
    }

    public handleCreateNewUserResumeButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const userObjId = Formatters.getStr(this.props, 'userObjId');

        const changeData = _.merge(
            {},
            { user: _.pick(this.props.userDetail, ['username', '_id']) }
        );

        this.props.newUserResume(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                // const ud = Formatters.getStr(this.props, 'userDetail._id');
                // this.props.navigateTo(NavigationLinks.userDetailObjId(ud));
                window.location.reload();
            } else {
                // Error during creation...
            }
        });
    }

    public handleShowModalNewUser(e: any, formData: any) {
        let formChanges = _.cloneDeep(this.state.formChanges);
        const newUser = this.getDefaultNewUserObject(_.get(formData, 'userRoleOptions', []));
        _.set(formChanges, 'newUser', newUser);

        this.setState({ formChanges: formChanges, showModalNewUser: true });
    }

    public handleFormInputChange(e: any, x: any) {
        const getChangesResult = this.getFormChanges(e, x);
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    public handleFormRoleCheckboxChange(e: any, x: any) {
        const roleObjId = Formatters.getStr(e, 'target.props.name');
        const roleNewAssigned = _.get(e, 'value') ? true : false;

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups);

        let filteredRolesAdd = _.reject(_.cloneDeep(_.get(formData, 'rolesAdd', [])), (o) => {
            const match = (String(o) === String(roleObjId)) ? true : false;
            return match;
        });
        let filteredRolesDelete = _.reject(_.cloneDeep(_.get(formData, 'rolesDelete', [])), (o) => {
            const match = (String(o) === String(roleObjId)) ? true : false;
            return match;
        });

        let userRolesChanges = _.stubArray();
        if (!roleNewAssigned) {
            // Delete role
            filteredRolesDelete.push(roleObjId);
            userRolesChanges.push({ fieldName: 'rolesAdd', newValue: _.cloneDeep(filteredRolesAdd) });
            userRolesChanges.push({ fieldName: 'rolesDelete', newValue: _.cloneDeep(filteredRolesDelete) });
        } else {
            // Add role
            filteredRolesAdd.push(roleObjId);
            userRolesChanges.push({ fieldName: 'rolesAdd', newValue: _.cloneDeep(filteredRolesAdd) });
            userRolesChanges.push({ fieldName: 'rolesDelete', newValue: _.cloneDeep(filteredRolesDelete) });
        }

        if (_.size(userRolesChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userRolesChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleFormUserGroupCheckboxChange(e: any, x: any) {
        const userGroupObjId = Formatters.getStr(e, 'target.props.name');
        const newlyAssigned = _.get(e, 'value') ? true : false;

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups, ...rest } = this.props;
        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups);

        let filteredAdd = _.reject(_.cloneDeep(_.get(formData, 'groupsAdd', [])), (o) => {
            const match = (String(o) === String(userGroupObjId)) ? true : false;
            return match;
        });

        let filteredDelete = _.reject(_.cloneDeep(_.get(formData, 'groupsDelete', [])), (o) => {
            const match = (String(o) === String(userGroupObjId)) ? true : false;
            return match;
        });

        let userGroupChanges = _.stubArray();
        if (!newlyAssigned) {
            // Delete userGroup
            filteredDelete.push(userGroupObjId);
            userGroupChanges.push({ fieldName: 'groupsAdd', newValue: _.cloneDeep(filteredAdd) });
            userGroupChanges.push({ fieldName: 'groupsDelete', newValue: _.cloneDeep(filteredDelete) });
        } else {
            // Add userGroup
            filteredAdd.push(userGroupObjId);
            userGroupChanges.push({ fieldName: 'groupsAdd', newValue: _.cloneDeep(filteredAdd) });
            userGroupChanges.push({ fieldName: 'groupsDelete', newValue: _.cloneDeep(filteredDelete) });
        }

        if (_.size(userGroupChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userGroupChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleFormRegionCheckboxChange(e: any, x: any) {
        const regionObjId = Formatters.getStr(e, 'target.props.name');
        const newlyAssigned = _.get(e, 'value') ? true : false;

        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups);

        let filteredAdd = _.reject(_.cloneDeep(_.get(formData, 'regionsAdd', [])), (o) => {
            const match = (String(o) === String(regionObjId)) ? true : false;
            return match;
        });
        let filteredDelete = _.reject(_.cloneDeep(_.get(formData, 'regionsDelete', [])), (o) => {
            const match = (String(o) === String(regionObjId)) ? true : false;
            return match;
        });

        let userChanges = _.stubArray();
        if (!newlyAssigned) {
            // Delete role
            filteredDelete.push(regionObjId);
            userChanges.push({ fieldName: 'regionsAdd', newValue: _.cloneDeep(filteredAdd) });
            userChanges.push({ fieldName: 'regionsDelete', newValue: _.cloneDeep(filteredDelete) });
        } else {
            // Add role
            filteredAdd.push(regionObjId);
            userChanges.push({ fieldName: 'regionsAdd', newValue: _.cloneDeep(filteredAdd) });
            userChanges.push({ fieldName: 'regionsDelete', newValue: _.cloneDeep(filteredDelete) });
        }

        if (_.size(userChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    // TODO: encode html when storing in the database
    public handleFormResumeInputChange(e: any) {
        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups, ...rest } = this.props;

        const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups);

        const prevResumeHtml = Formatters.getStr(formData, 'userResume.resume');
        const newResumeHtml = String(e).replace('&nbsp;', ' ').replace('<p>', '').replace('</p>', '');

        let userResumeChanges = _.stubArray();
        if (prevResumeHtml !== newResumeHtml) {
            userResumeChanges.push({ fieldName: 'userResume.resume', newValue: newResumeHtml });
        }

        if (_.size(userResumeChanges)) {
            let cloneFormChanges = _.cloneDeep(this.state.formChanges);

            userResumeChanges.forEach((o) => {
                _.set(cloneFormChanges, o.fieldName, _.cloneDeep(o.newValue));
            });

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    /*     public handleShowModalModifyGroups(e: any, x: any) {
            let formChanges = _.cloneDeep(this.state.formChanges);
            this.setState({ formChanges: formChanges, showModalModifyGroups: true });
        }
    
        public handleCloseModalModifyGroups() {
            this.setState({ showModalModifyGroups: false });
        } */

    // HELPERS
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                case 'email':
                case 'password':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            // if (String(newValue) !== String(origVal)) {
            // New value
            _.set(formChangesCopy, fieldName, newValue);
            _.set(formChangesResult, fieldName, newValue);
            applyChange = true;
            // } else {
            //     // Same as orig val
            //     if (_.has(formChangesCopy, fieldName)) {
            //         _.unset(formChangesCopy, fieldName);
            //         _.set(formChangesResult, fieldName, newValue);
            //         applyChange = true;
            //     }
            // }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getFormData(userIn: any, userResume: any, roles: any, users: any, portalInvites: any, regions: any, usersCurrentGroups: any, allGroups: any) {
        const user = _.merge({}, userIn, this.state.formChanges);

        const userRoleOptions = (_.size(roles)) ?
            roles.map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'name'),
                assigned: false,
            })) : [];

        const userRegionOptions = (_.size(regions)) ?
            regions.map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'name'),
                assigned: false,
            })) : [];

        const userGroups = (_.size(usersCurrentGroups)) ?
            _.orderBy(usersCurrentGroups, ['description'], ['asc']).map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'description'),
                assigned: true,
            })) : [];

        const userGroupOptions = (_.size(allGroups)) ?
            _.orderBy(allGroups, ['description'], ['asc']).map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'description'),
                assigned: false,
            })) : [];

        let newUser = _.get(user, 'newUser', this.getDefaultNewUserObject(userRoleOptions));

        /* let userAdditionalGroupOptions: any = _.stubArray();
        if (_.size(allGroups)) {
            userAdditionalGroupOptions = _.orderBy(allGroups, ['description'], ['asc']).map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.getStr(c, 'description') }));
        } */

        let result = {
            username: Formatters.getStr(user, 'username'),
            contact: _.get(user, 'contact'),
            email: Formatters.getStr(user, 'email'),
            inactive: _.get(user, 'inactive'),
            lastLogin: _.get(user, 'lastLogin'),
            locked: _.get(user, 'locked'),
            signaturePictureFilename: Formatters.getStr(user, 'signaturePictureFilename'),
            profileSettings: {
                hideReplacementCosts: Formatters.getBool(user, 'profileSettings.hideReplacementCosts'),
            },

            portalInvites: (_.size(portalInvites)) ? portalInvites : [],

            roles: _.get(user, 'roles', []),
            rolesExisting: _.map(_.get(userIn, 'roles', []), 'roleObjectId'),
            rolesAdd: _.get(user, 'rolesAdd', []),
            rolesDelete: _.get(user, 'rolesDelete', []),

            regions: _.get(user, 'regions', []),
            regionsExisting: _.map(_.get(userIn, 'regions', []), 'regionObjectId'),
            regionsAdd: _.get(user, 'regionsAdd', []),
            regionsDelete: _.get(user, 'regionsDelete', []),

            // groups: _.get(user, 'usersCurrentGroups', []),
            groups: (_.size(userGroups)) ? userGroups : [],
            groupsExisting: _.map(userGroups, 'value'),
            groupsAdd: _.get(user, 'groupsAdd', []),
            groupsDelete: _.get(user, 'groupsDelete', []),

            resetPassword: Formatters.getStr(user, 'resetPassword'),
            resetPasswordConfirm: Formatters.getStr(user, 'resetPasswordConfirm'),
            passwordChangeRequired: Formatters.getBool(user, 'passwordChangeRequired'),

            newUser: newUser,

            userResume: userResume,
            userRoleOptions: (_.size(userRoleOptions)) ? userRoleOptions : [],
            userRegionOptions: (_.size(userRegionOptions)) ? userRegionOptions : [],

            users: (_.size(users)) ? users : [],
            userGroupOptions: (_.size(userGroupOptions)) ? userGroupOptions : [],
            // userAdditionalGroupOptions: (_.size(userAdditionalGroupOptions)) ? userAdditionalGroupOptions : [],
            allGroups: (_.size(allGroups)) ? allGroups : [],

        };

        if ((_.size(result.rolesExisting) || _.size(result.rolesAdd) || _.size(result.rolesDelete)) && _.size(result.userRoleOptions)) {
            _.forEach(result.userRoleOptions, (o) => {
                const roleObjId = Formatters.getStr(o, 'value');

                const isExisting = (_.includes(result.rolesExisting, roleObjId)) ? true : false;
                const isAdded = (_.includes(result.rolesAdd, roleObjId)) ? true : false;
                const isDeleted = (_.includes(result.rolesDelete, roleObjId)) ? true : false;

                const isAssigned = ((isExisting || isAdded) && !isDeleted) ? true : false;
                // console.log(`userRoleOptions.map value ${o.value} ${o.text}: isAssigned ${isAssigned} - ((isExisting ${isExisting} || isAdded ${isAdded})) && !isDeleted ${isDeleted}`);

                if (o.assigned !== isAssigned) {
                    // console.log(`userRoleOptions.map changing value ${roleObjId} ${o.text}: assigned from ${o.assigned} to ${isAssigned}`);
                    _.set(o, 'assigned', isAssigned);
                }
            });
        }

        if ((_.size(result.groupsExisting) || _.size(result.groupsAdd) || _.size(result.groupsDelete)) && _.size(result.userGroupOptions)) {
            _.forEach(result.userGroupOptions, (o) => {
                const groupObjId = Formatters.getStr(o, 'value');

                const isExisting = (_.includes(result.groupsExisting, groupObjId)) ? true : false;
                const isAdded = (_.includes(result.groupsAdd, groupObjId)) ? true : false;
                const isDeleted = (_.includes(result.groupsDelete, groupObjId)) ? true : false;

                const isAssigned = ((isExisting || isAdded) && !isDeleted) ? true : false;

                if (o.assigned !== isAssigned) {
                    _.set(o, 'assigned', isAssigned);
                }
            });
        }

        if ((_.size(result.regionsExisting) || _.size(result.regionsAdd) || _.size(result.regionsDelete)) && _.size(result.userRegionOptions)) {
            _.forEach(result.userRegionOptions, (o) => {
                const regionObjId = Formatters.getStr(o, 'value');

                const isExisting = (_.includes(result.regionsExisting, regionObjId)) ? true : false;
                const isAdded = (_.includes(result.regionsAdd, regionObjId)) ? true : false;
                const isDeleted = (_.includes(result.regionsDelete, regionObjId)) ? true : false;

                const isAssigned = ((isExisting || isAdded) && !isDeleted) ? true : false;

                if (o.assigned !== isAssigned) {
                    _.set(o, 'assigned', isAssigned);
                }
            });
        }

        return _.merge({}, result, this.state.formChanges);
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getStr(this.props, 'userObjId');

        // Cached values
        const ca = Formatters.getStr(this.originalFormDataCache, 'userObjId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    public getDefaultNewUserObject(userRoleOptions: any) {
        return _.cloneDeep({
            username: '',
            password: '',
            passwordConfirm: '',
            passwordChangeRequired: false,
            email: '',
            firstName: '',
            lastName: '',
            title: '',
            inactive: false,
            locked: false,
            profileSettings: {
                hideReplacementCosts: false,
            },
            userRolesAssigned: _.filter(userRoleOptions, (o) => _.includes(DefaultNewUserRoleIds, _.get(o, 'value', []))),
            // userRegionsAssigned: _.filter(userRegionOptions, (o) => _.includes(DefaultNewUserRegionIds, _.get(o, 'value', [])))
        });
    }

    // RENDERERS
    public renderHeaderButtonToolbar = (formData: any) => {
        const userResumeObj = _.get(formData, 'userResume');
        const userResumeIsEmpty = Object.keys(userResumeObj).length === 0;

        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }

        const auth = new Auth();
        const isAdmin = (auth.isAdmin()) ? true : false;

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={12} xl={12}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button {...saveChangesButtonProps} onClick={e => { this.handleSaveButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                                </ButtonGroup>

                                <ButtonGroup>
                                    {/* <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleShowModalNewUser(e, formData); }}> */}
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleCreateNewUserButton(e); }}>
                                        <i className="glyphicon glyphicon-plus" /> {this.appText('new-user')}
                                    </Button>
                                </ButtonGroup>

                                <ButtonGroup>
                                    {(isAdmin && userResumeIsEmpty) ? (
                                        <Button bsSize="sm" bsStyle="warning" onClick={e => { this.handleCreateNewUserResumeButton(e); }}>
                                            <i className="glyphicon glyphicon-plus" /> {this.appText('new-user-resume')}
                                        </Button>
                                    ) : (<div />)}
                                </ButtonGroup>

                                <ButtonGroup>
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleViewProfileButton(e); }}>
                                        <i className="fa fa-user" /> {this.appText('view-profile')}
                                    </Button>
                                </ButtonGroup>

                                <ButtonGroup>
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleSearchUsersButton(e); }}>
                                        <i className="fa fa-search" /> {this.appText('search-users')}
                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSectionUserContactDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            // onChange: (n, v) => { handleFormInputChange(n, v); },
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('contact-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('contact-details')}>
                                <KendoWrappers.KendoTextInputDebounced
                                    name="contact.firstName"
                                    label={this.appText('contact-first-name')}
                                    value={Formatters.getStr(formData, 'contact.firstName')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoTextInputDebounced
                                    name="contact.lastName"
                                    label={this.appText('contact-last-name')}
                                    value={Formatters.getStr(formData, 'contact.lastName')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoTextInputDebounced
                                    name="contact.title"
                                    label={this.appText('contact-title')}
                                    value={Formatters.getStr(formData, 'contact.title')}
                                    {...inputPropsFullWidthTextDebounced}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserLoginDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('login-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('login-details')}>
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_login-username"
                                    label={this.appText('login-username')}
                                    value={Formatters.getStr(formData, 'username')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoTextInputDebounced
                                    name="email"
                                    label={this.appText('login-email')}
                                    value={Formatters.getStr(formData, 'email')}
                                    type="email"
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_login-lastlogin"
                                    label={this.appText('login-last-login')}
                                    value={Formatters.formatDateLongDate(Formatters.getStr(formData, 'lastLogin'), this.appText('login-never'))}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_login-reset-password"
                                    label={this.appText('login-password')}
                                    value=""
                                    {...inputPropsFullWidthTextDebounced}
                                >
                                    <div className="edit-order-set-due-date-button-wrapper">
                                        <Button
                                            bsSize="xs"
                                            bsStyle="info"
                                            onClick={e => { this.handleShowModalResetPassword(e, formData); }}
                                        >
                                            <i className="fa fa-lock" /> {this.appText('reset-password')}
                                        </Button>
                                    </div>
                                </KendoWrappers.KendoDisabledInputHidden>

                                <KendoWrappers.KendoCheckbox
                                    name="passwordChangeRequired"
                                    label={this.appTextCommon('forms.user.password.must-change-password')}
                                    value={Formatters.getBool(formData, 'passwordChangeRequired')}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserProfileSettings(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormRoleCheckboxChange(n, v); },
            colInputXs: 7,
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('profile-settings')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('profile-settings')}>
                                <KendoWrappers.KendoCheckbox
                                    name="profileSettings.hideReplacementCosts"
                                    label={this.appText('hide-replacement-costs')}
                                    value={formData.profileSettings.hideReplacementCosts}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserPortalAccessDetails(formData: any) {
        const allPortalInvites = _.get(formData, 'portalInvites', []);

        // if (!_.size(allPortalInvites)) { return (<div />); }

        const auth = new Auth();
        const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;

        const showDeactivePortalInvites = (isStaff) ? true : false;

        const validPortalInvites: any = UserUtilities.GetValidPortalInvites(allPortalInvites);
        const deactivePortalInvites: any = UserUtilities.GetInvalidPortalInvites(allPortalInvites);

        // console.log('renderSectionUserPortalAccessDetails', {formData, allPortalInvites, validPortalInvites, deactivePortalInvites});

        // if (!_.size(validPortalInvites)) { return (<div />); }

        const filterAdminRoleInvites = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalAdmin.RoleName);
        const invitesAdmin = _.map(filterAdminRoleInvites, (o) => {
            const assocId = _.get(o, 'association.associationId', 0);
            // const assocLink = NavigationLinks.associationAnalysis(assocId);
            const assocLink = NavigationLinks.portalAssociationReport(assocId);

            return UserUtilities.BuildInviteLinkToAssociation(o, assocLink, true);
        });
        const portalAccessAdmin = (_.size(invitesAdmin)) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('portal-role-admin')}>
                {invitesAdmin}
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        const filterUserRoleInvites = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalUser.RoleName);
        const invitesUser = _.map(filterUserRoleInvites, (o) => {
            const assocId = _.get(o, 'association.associationId', 0);
            // const assocLink = NavigationLinks.associationAnalysis(assocId);
            const assocLink = NavigationLinks.portalAssociationReport(assocId);
            return UserUtilities.BuildInviteLinkToAssociation(o, assocLink, true);
        });
        const portalAccessUser = (_.size(invitesUser)) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('portal-role-user')}>
                {invitesUser}
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        // console.log('filterUser', {filterUser: filterUserRoleInvites, rolesUser: invitesUser});

        const invitesDeactive = _.map(deactivePortalInvites, (o) => {
            const assocId = _.get(o, 'association.associationId', 0);
            // const assocLink = NavigationLinks.associationAnalysis(assocId);
            const assocLink = NavigationLinks.portalAssociationReport(assocId);
            return UserUtilities.BuildInviteLinkToAssociation(o, assocLink, true);
        });
        const portalAccessDeactive = (showDeactivePortalInvites && _.size(invitesDeactive)) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('portal-invite-deactive')}>
                {invitesDeactive}
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('portal-access')}>
                <Row>
                    <Col xs={12}>
                        <div className="user-edit-portal-invites">
                            {portalAccessAdmin}
                            {portalAccessUser}
                            {portalAccessDeactive}
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserSecurityDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormRoleCheckboxChange(n, v); },
            colInputXs: 7,
        });

        const inputPropsRegionCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormRegionCheckboxChange(n, v); },
            colInputXs: 7,
        });

        const inputPropsGroupsCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormUserGroupCheckboxChange(n, v); },
            colInputXs: 7,
        });

        let roleCheckboxes = _.get(formData, 'userRoleOptions', []).map((c) => {
            const hasRoleAssigned = (_.get(c, 'assigned', false)) ? true : false;
            return (
                <KendoWrappers.KendoCheckbox
                    key={`role_${c.value}`}
                    name={c.value}
                    label={Formatters.getStr(c, 'text')}
                    value={hasRoleAssigned}
                    {...inputPropsRoleCheckbox}
                />
            );
        });

        let regionCheckboxes = _.get(formData, 'userRegionOptions', []).map((c) => {
            const hasRegionAssigned = (_.get(c, 'assigned', false)) ? true : false;
            return (
                <KendoWrappers.KendoCheckbox
                    key={`region_${c.value}`}
                    name={c.value}
                    label={Formatters.getStr(c, 'text')}
                    value={hasRegionAssigned}
                    {...inputPropsRegionCheckbox}
                />
            );
        });

        let groupsCheckboxes = _.get(formData, 'userGroupOptions', []).map((c) => {
            const hasGroupAssigned = (_.get(c, 'assigned', false)) ? true : false;
            return (
                <KendoWrappers.KendoCheckbox
                    key={`group_${c.value}`}
                    name={c.value}
                    label={Formatters.getStr(c, 'text')}
                    value={hasGroupAssigned}
                    {...inputPropsGroupsCheckbox}
                />
            );
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('security-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-details')}>
                                <KendoWrappers.KendoCheckbox
                                    name="inactive"
                                    label={this.appText('security-inactive')}
                                    value={formData.inactive}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoCheckbox
                                    name="locked"
                                    label={this.appText('security-locked')}
                                    value={formData.locked}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-roles')}>
                                {roleCheckboxes}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-regions')}>
                                {regionCheckboxes}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-groups')}>
                                {groupsCheckboxes}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionUserResumeDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 2,
            colInputXs: 2,
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsHtmlEditor = _.merge({}, defaultInputProps, {
            onChange: (n) => { this.handleFormResumeInputChange(n); },
            colLabelXs: 12,
            colInputXs: 12,
            className: 'inventory-detail-input-full-width',
        });

        const userDetailRoles = _.get(formData, 'userRoleOptions', []);
        const isAdminOrEmployee = ((UserComponents.HasAssignedRole(AuthConfig.UserRoles.administrator, userDetailRoles)) || UserComponents.HasAssignedRole(AuthConfig.UserRoles.employee, userDetailRoles)) ? true : false;
        const isClient = ((UserComponents.HasAssignedRole(AuthConfig.UserRoles.client, userDetailRoles)) || UserComponents.HasAssignedRole(AuthConfig.UserRoles.clientReadOnly, userDetailRoles)) ? true : false;
        if (!isAdminOrEmployee && isClient) {
            return (<div />);
        }

        // Signature
        const signaturePath = Branding.getBrandUserSignaturePicture(Formatters.getStr(formData, 'signaturePictureFilename'), Branding.appBrandKeys.complexSolutions);
        const signatureNotFoundPath = Branding.getBrandUserSignaturePicture(Branding.appBrandImageFileNames.signatureNotFound, Branding.appBrandKeys.complexSolutions);

        const signatureImageObj = {
            src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signaturePath, true, 'h_55,f_auto/'),
            fileName: signaturePath,
            fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signatureNotFoundPath, true, 'h_55,f_auto/'),
        };

        const signaturePath2 = Branding.getBrandUserSignaturePicture(Formatters.getStr(formData, 'signaturePictureFilename'), Branding.appBrandKeys.appliedReserve);
        const signatureNotFoundPath2 = Branding.getBrandUserSignaturePicture(Branding.appBrandImageFileNames.signatureNotFound, Branding.appBrandKeys.appliedReserve);

        const signatureImageObj2 = {
            src: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signaturePath2, true, 'h_55,f_auto/'),
            fileName: signaturePath2,
            fallbackSrc: ApiEndpoints.getBidLetterUserSignaturePictureUrl(signatureNotFoundPath2, true, 'h_55,f_auto/'),
        };

        const RenderSignaturePanel = ({ brandKey, sigImgObj, ...props }) => {
            return (
                <div className="inline-block resume-signature-picture-wrapper">
                    <div className="resume-signature-picture-brand-name">
                        {Branding.getBrandCompanyName(brandKey)}
                    </div>

                    <hr className="resume-signature-picture-hr" />

                    <div className="resume-signature-picture-image-wrapper text-center">
                        <ReactImageFallback
                            src={sigImgObj.src}
                            fallbackImage={sigImgObj.fallbackSrc}
                            className="resume-signature-picture-image"
                        />
                    </div>

                    <div className="resume-signature-picture-filename">
                        {sigImgObj.fileName}
                    </div>
                </div>
            );
        };

        const userResumeObj = _.get(formData, 'userResume');
        const userResumeIsEmpty = Object.keys(userResumeObj).length === 0;

        return (userResumeIsEmpty) ? (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('resume-details')}>
                <Row>
                    <Col xs={12}>
                        <div>This user has no resume configured. Admins can generate a template resume to edit by clicking the 'Create Missing User Resume' button at the top of the page.</div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        ) : (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('resume-details')}>
                <Row>
                    <Col xs={12}>
                        <div className="user-edit-resume-details">
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('resume-details')}>

                                <KendoWrappers.KendoTextInputDebounced
                                    name="userResume.permits[0].permitNumber"
                                    label={this.appText('resume-permit-number-nv')}
                                    value={Formatters.getStr(formData, 'userResume.permits[0].permitNumber')}
                                    {...inputPropsFullWidthTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_user-signature"
                                    label={this.appText('resume-signature')}
                                    value={[
                                        <RenderSignaturePanel key="sig-1" sigImgObj={signatureImageObj} brandKey={Branding.appBrandKeys.complexSolutions} />,
                                        <RenderSignaturePanel key="sig-2" sigImgObj={signatureImageObj2} brandKey={Branding.appBrandKeys.appliedReserve} />
                                    ]}
                                    {...inputPropsHtmlEditor}
                                />

                                <KendoWrappers.KendoHtmlEditor
                                    name="userResume.resume"
                                    label={this.appText('resume-text')}
                                    defaultValue={Formatters.getStr(formData, 'userResume.resume')}
                                    {...inputPropsHtmlEditor}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderResetPasswordModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 6 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            // onChange: (n, v) => { handleFormInputChange(n, v); },
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        const submitButtonDisabled = (
            (_.size(Formatters.getStr(formData, 'resetPassword')) === 0 || _.size(Formatters.getStr(formData, 'resetPassword')) >= UserComponents.MinimumPasswordLength) &&
            Formatters.getStr(formData, 'resetPassword') === Formatters.getStr(formData, 'resetPasswordConfirm') &&
            (_.size(Formatters.getStr(formData, 'resetPasswordConfirm')) === 0 || _.size(Formatters.getStr(formData, 'resetPasswordConfirm')) >= UserComponents.MinimumPasswordLength)
        ) ? false : true;

        return (
            <Modal show={this.state.showModalResetPassword} onHide={() => this.handleCloseModalResetPassword()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.reset-password.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div>
                        <DetailFormCommon.InventoryDetailFormSection title="">
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_reset-password-username"
                                label={this.appText('login-username')}
                                value={Formatters.getStr(formData, 'username')}
                                {...inputPropsFullWidthTextDebounced}
                            />

                            <DetailFormCommon.InventoryDetailFormSectionSpacing />

                            <KendoWrappers.KendoTextInputDebounced
                                name="resetPassword"
                                label={this.appText('modals.reset-password.new-password')}
                                value={Formatters.getStr(formData, 'resetPassword')}
                                type="password"
                                {...inputPropsFullWidthTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="resetPasswordConfirm"
                                label={this.appText('modals.reset-password.new-password-confirm')}
                                value={Formatters.getStr(formData, 'resetPasswordConfirm')}
                                type="password"
                                {...inputPropsFullWidthTextDebounced}
                            />

                            <DetailFormCommon.InventoryDetailFormSectionSpacing />

                            <KendoWrappers.KendoCheckbox
                                name="locked"
                                label={this.appText('modals.reset-password.unlock-if-locked-out')}
                                value={_.get(formData, 'locked', false) ? true : false}
                                {...inputProps}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="success"
                        disabled={submitButtonDisabled}
                        onClick={(e) => this.handleCloseModalResetPassword()}
                    >
                        {this.appText('modals.reset-password.set-changes')}
                    </Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalResetPassword()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { userObjId, userDetail: user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups, ...rest } = this.props;

        let pageTitle = this.appTextPageTitleAppendString(userObjId, 'page-title', true);
        let page = (<div />);

        if (Formatters.getStr(user, '_id') === userObjId) {
            const formData = this.getFormData(user, userResume, roles, users, portalInvites, regions, usersCurrentGroups, allGroups);

            // console.log('pageData', {
            //     username: username,
            //     user: user,
            //     formData: formData,
            //     numChanges: this.getFormChangesCount(),
            //     formChanges: this.state.formChanges,
            // });

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            pageTitle = this.appTextPageTitleAppendString(Formatters.composeUsernameLabel(user, true), 'page-title.edit', true);
            page = (
                <div>
                    {this.renderResetPasswordModal(formData)}
                    <Row>
                        <Col xs={12} xl={9}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar(formData)}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={5} xs={6}>
                                    {/* LOGIN DETAILS */}
                                    <Row>
                                        <Col xs={12}>
                                            <div>
                                                {this.renderSectionUserLoginDetails(formData)}
                                                {/* validation on email field */}
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* CONTACT DETAILS */}
                                    <Row>
                                        <Col xs={12}>
                                            <div>
                                                {this.renderSectionUserContactDetails(formData)}
                                                {/* middle initial */}
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col xl={7} xs={6}>
                                    {/* PROFILE SETTINGS */}
                                    <Row>
                                        <Col xs={12}>
                                            <div>
                                                {this.renderSectionUserProfileSettings(formData)}
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* SECURITY DETAILS */}
                                    <Row>
                                        <Col xs={12}>
                                            <div>
                                                {this.renderSectionUserSecurityDetails(formData)}
                                                {/* allow assign roles */}
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* PORTAL ACCESS/INVITES */}
                                    <Row>
                                        <Col xs={12}>
                                            <div>
                                                {this.renderSectionUserPortalAccessDetails(formData)}
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            {/* RESUME DETAILS */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderSectionUserResumeDetails(formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getUserDetailUrl(Formatters.getStr(props, 'match.params.userObjId'));

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getUserDetailRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const userObjId = Formatters.getStr(props, 'match.params.userObjId');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        userDetail: state.entities.userDetailUser,
        userResume: state.entities.userDetailUserResume,
        roles: state.entities.userDetailRoles,
        regions: state.entities.userDetailRegions,
        users: state.entities.userDetailUsers,
        portalInvites: state.entities.userDetailPortalInvites,
        userObjId: userObjId,
        usersCurrentGroups: state.entities.userDetailGroups,
        allGroups: state.entities.userDetailAllGroups,
    });
};

const mapDispatchToProps = {
    updateUser: MutateActions.updateUserMutateAsync,
    newUser: MutateActions.newUserMutateAsync,
    newUserResume: MutateActions.newUserResumeMutateAsync,
    navigateTo: (path) => push(path),
};

const EditUserContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(EditUser);

export default withRouter(EditUserContainer);



// WEBPACK FOOTER //
// ./src/user/edit.tsx